import './ReviewTask.scss';
import React, {useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useHistory} from 'react-router-dom';
import {FmlxButton, FmlxCheckBox, FmlxChip, FmlxIcon} from 'fmlx-common-ui';
import {ALERT_VARIANT, EPathname, EResponseCode, EReviewStatus, ERoleId, RESPONSE_STATUS} from '../../const/dataEnum';
import {FMLX_BUTTON, FMLX_CHIP, FMLX_COMPONENT} from '../../const/FmlxComponentEnum';
import {setAlert} from '../../stores/actions/alertModalStateAction';
import {removeWhitespace} from '../../lib/utilities';
import employeeReviewApi from '../../api/rest/employeeReviewApi';
import EssSimpleTable from '../../components/MessageTable/TableColumn/EssSimpleTable';

export default function ReviewTask() {
  const dispatch = useDispatch();
  const {currentPeriodId, currentPeriodActive} = useSelector((state) => ({
    currentPeriodId: state.appState.currentPeriod.id,
    currentPeriodActive: state.appState.currentPeriod.isActive,
  }));

  const fileInput = useRef();
  const revieweeInputRef = useRef();
  const debounceTimerRef = useRef(null);
  const userAuthRoleId = useSelector((s) => s.appState.user?.authRole?.id);

  const [reviewTasks, setReviewTask] = useState([]);
  const [responseStatus, setResponseStatus] = useState(RESPONSE_STATUS.IDLE);
  const [selectedRowIds, setSelectedRowIds] = useState([]);
  const [sortValue, setSortValue] = useState(null);
  const [filterMap, setFilterMap] = useState({});
  const [selectedItemsState, setSelectedItemsState] = useState([]);
  const [pageSizeState, setPageSizeState] = useState(10);
  const [pageNoState, setPageNoState] = useState(0);
  const [totalItem, setTotalItem] = useState(0);
  const pageSize = [10, 20, 30];
  const [filterSelectedIndex, setFilterSelectedIndex] = useState({status: -1});
  const [dropdownDisabled, setDropdownDisabled] = useState(false);

  const history = useHistory();

  useEffect(() => {
    let timer = null;
    if (currentPeriodId) {
      timer = setTimeout(() => {
        populateData();
      }, 300);
    }
    return () => {
      clearTimeout(timer);
    }
  }, [currentPeriodId, sortValue, filterMap, pageNoState, pageSizeState]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  async function populateData() {
    setResponseStatus(RESPONSE_STATUS.PENDING);
    try {
      const response = await employeeReviewApi.getEmployeeReviewSummary(currentPeriodId, {
        sortBy: sortValue?.column,
        sortType: sortValue?.type,
        pageNo: pageNoState + 1,
        pageSize: pageSizeState,
        fullname: filterMap?.reviewee || null,
        status: filterMap?.status || null,
      });

      const {data, totalCount, message, responseCode} = response;

      if (responseCode !== 0 || !data) {
        const errorMessage = message || 'Failed to load review summary. Data is unavailable.';
        dispatch(setAlert({ show: true, variant: ALERT_VARIANT.ERROR, message: errorMessage }));
        setReviewTask([]);
        setTotalItem(0);
        setResponseStatus(RESPONSE_STATUS.ERROR);
        return; 
      }

      setTotalItem(totalCount);
      const processedData = data.map((row) => ({
        ...row,
        status:
          row.totalAnswered === 0
            ? EReviewStatus.NOT_STARTED
            : row.mandatoryUnAnswered === 0
            ? EReviewStatus.COMPLETE
            : EReviewStatus.INCOMPLETE,
      }));
      setReviewTask(processedData.filter(task => task.reviewType.trim().toLowerCase() !== 'consolidation'));
      setResponseStatus(RESPONSE_STATUS.SUCCESS);
    } catch (error) {
      setReviewTask([]);
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.data}));
      setResponseStatus(RESPONSE_STATUS.ERROR);
    } finally {
      setResponseStatus(RESPONSE_STATUS.SUCCESS);
      setDropdownDisabled(false);
    }
  }

  function decode_utf8(s) {
    return decodeURIComponent(escape(s));
  }

  function ab2str(buf) {
    var s = String.fromCharCode.apply(null, new Uint8Array(buf));
    return decode_utf8(decode_utf8(s));
  }

  const onClickDownloadTemplate = async () => {
    try {
      const employeeIds = selectedItemsState.map((item) => item.id);
      const response = await employeeReviewApi.getBulkReviewTemplate(employeeIds);
      if (!response) return;
      const contentType = response.headers['content-type'];
      if (contentType.includes('application/json')) {
        const jsonError = JSON.parse(ab2str(response.data));
        if (jsonError?.responseCode !== 0) {
          dispatch(
            setAlert({
              show: true,
              variant: ALERT_VARIANT.ERROR,
              message: jsonError.message,
            })
          );
        }
        return;
      }
      let filename = 'Bulk_review.xlsx';
      const disposition = response.headers['content-disposition'];
      if (disposition?.includes('attachment')) {
        const match = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(disposition);
        if (match?.[1]) {
          filename = match[1].replace(/['"]/g, '');
        }
      }
      const blob = new Blob([response.data], {type: contentType});
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = filename;
      link.click();
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.message}));
    }
  };

  const onUploadReview = async (files) => {
    try {
      const response = await employeeReviewApi.uploadBulkReview(files);
      const {data, message, responseCode} = response;
      if (responseCode === EResponseCode.NO_ERROR) {
        dispatch(setAlert({show: true, variant: ALERT_VARIANT.SUCCESS, message: `${message}`}));
      } else if (responseCode !== EResponseCode.NO_ERROR) {
        dispatch(
          setAlert({
            show: true,
            variant: ALERT_VARIANT.ERROR,
            message: data.map((error, i) => {
              return <div key={i}>{error.message}</div>;
            }),
            timeout: null,
          })
        );
      }
    } catch (error) {
      dispatch(setAlert({show: true, variant: ALERT_VARIANT.ERROR, message: error.data || error.message}));
    } finally {
      if (currentPeriodId) populateData(currentPeriodId.id);
    }
  };

  const handleFileChosen = (e) => {
    var filesArray = [...e.target.files];
    let f = new FormData();
    for (let i = 0; i < filesArray.length; i++) {
      f.append("file", filesArray[i]);
    }
    fileInput.current.value = "";
    onUploadReview(f);
  };

  const disableBulkReviewBtn = () => {
    return !(currentPeriodActive && selectedRowIds.length > 0);
  };

  const select = (
      <div className="select-container">
        {userAuthRoleId !== ERoleId.TEAM_MEMBER ? (
          <>
            <FmlxButton
              size={FMLX_COMPONENT.SIZE.M}
              type={FMLX_BUTTON.TYPE.PRIMARY}
              variant={FMLX_BUTTON.VARIANT.OUTLINE}
              disabled={disableBulkReviewBtn()}
              label="GENERATE TEMPLATE"
              onClick={onClickDownloadTemplate}
            />
            <FmlxButton
              size={FMLX_COMPONENT.SIZE.M}
              type={FMLX_BUTTON.TYPE.PRIMARY}
              variant={FMLX_BUTTON.VARIANT.CONTAIN}
              disabled={disableBulkReviewBtn()}
              label="UPLOAD REVIEW"
              onClick={() => {
                fileInput.current.click();
              }}
            />
          </>
        ) : (
          <></>
        )}
        <input name="upload" type="file" accept=".xlsx" onChange={handleFileChosen} ref={fileInput} />
      </div>
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const columns = [
    {
      id: 0,
      accessor: 'CHECKBOX',
      Header: '',
      sortable: false,
      hideFilter: true,
      renderCell: (col, row, index) => (
        <FmlxCheckBox checked={selectedRowIds.includes(row.id)} onChange={(e) => handleCheckBoxChange(e, row.id)} />
      ),
    },
    {
      id: 1,
      Header: 'Reviewee',
      accessor: 'reviewee',
      filter: 'TEXT',
      sortable: false,
    },
    {
      Header: 'Role',
      accessor: 'role',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
    },
    {
      Header: 'Practice',
      accessor: 'practice',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
    },
    {
      Header: 'Level',
      accessor: 'level',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
    },
    {
      Header: 'Project',
      accessor: 'project',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
    },
    {
      Header: 'Relation',
      accessor: 'reviewType',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
    },
    {
      Header: 'Status',
      accessor: 'status',
      filter: 'DROPDOWN',
      sortable: false,
      items: [
        {id: 0, text: EReviewStatus.ALL, group: 'default', disabled: false},
        {id: 1, text: EReviewStatus.NOT_STARTED, group: 'default', disabled: false},
        {id: 2, text: EReviewStatus.INCOMPLETE, group: 'default', disabled: false},
        {id: 3, text: EReviewStatus.COMPLETE, group: 'default', disabled: false},
      ],
      renderCell: (column, row) => {
        return (
          <FmlxChip
            key={row.id}
            variant={FMLX_CHIP.VARIANT.FILLED}
            type={
              row.totalAnswered === 0
                ? FMLX_CHIP.TYPE.DEFAULT
                : row.mandatoryUnAnswered === 0
                ? FMLX_CHIP.TYPE.SAFE
                : FMLX_CHIP.TYPE.WARNING
            }
            label={row.totalAnswered === 0 ? 'Not Started' : row.mandatoryUnAnswered === 0 ? 'Complete' : 'Incomplete'}
          />
        );
      },
    },
    {
      Header: 'Action',
      accessor: 'actionDummy',
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
      renderCell: (column, row) => {
        return !row.locked ? (
          <FmlxButton
            size={FMLX_COMPONENT.SIZE.M}
            type={FMLX_BUTTON.TYPE.PRIMARY}
            variant={FMLX_BUTTON.VARIANT.CONTAIN}
            disabled={!(currentPeriodId && currentPeriodActive)}
            label="Review"
            onClick={() => {
              history.push({
                pathname: EPathname.REVIEW_QUESTION,
                state: {
                  reviewId: row.id,
                  departmentId: row.departmentId,
                  locked: false,
                },
              });
            }}
            withIcon={FMLX_BUTTON.WITH_ICON.END}
            icon={<FmlxIcon name="Editor" size={FmlxIcon.Size.SMALL} />}
          />
        ) : (
          <FmlxButton
            size={FMLX_COMPONENT.SIZE.M}
            variant={FMLX_BUTTON.VARIANT.OUTLINE}
            label="View"
            onClick={() => {
              history.push({
                pathname: EPathname.REVIEW_QUESTION,
                state: {
                  reviewId: row.id,
                  departmentId: row.departmentId,
                  locked: row.locked,
                },
              });
            }}
            withIcon={FMLX_BUTTON.WITH_ICON.END}
            icon={<FmlxIcon name="Eye" />}
          />
        );
      },
    },
    {
      Header: 'Updated on',
      accessor: 'reviewDate',
      editable: false,
      disableSort: true,
      hideSort: true,
      hideFilter: true,
      headerClassName: 'cell-header',
      columnClassName: 'cell-data',
      renderCell: (column, row) => {
        if (!row.reviewDate) {
          return <p className="lastDate">-</p>;
        }
        const formattedDate = new Intl.DateTimeFormat('id-ID', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
        }).format(new Date(row.reviewDate));

        return <p className="lastDate">{formattedDate}</p>;
      },
    },
  ];

  const modifyFilterMap = (filterMap) => {
    const newFilterMap = {};
    for (const key in filterMap) {
      if (filterMap.hasOwnProperty(key)) {
        const modifiedKey = removeWhitespace(key);
        let rawValue = filterMap[key];
        if (rawValue === undefined || rawValue === null) continue;

        let value = typeof rawValue === 'string' ? rawValue.toLowerCase() : rawValue;
        if (modifiedKey !== 'reviewee' && typeof rawValue === 'string') {
          value = removeWhitespace(rawValue);
        }

        newFilterMap[modifiedKey] = value;
      }
    }
    return newFilterMap;
  };

  const getSortValue = (sortValue) => {
    setSortValue(sortValue);
  };

  const getFilterMap = (filterMap) => {
    const newFilterMap = modifyFilterMap(filterMap);
    setFilterMap(newFilterMap);
  };

  const memoizedColumns = useMemo(() => columns, [columns]);

  const handleFilterDropdown = (e, accessor) => {
    setDropdownDisabled(true);
    const statusItems = [
      EReviewStatus.ALL,
      EReviewStatus.NOT_STARTED,
      EReviewStatus.INCOMPLETE,
      EReviewStatus.COMPLETE,
    ];
    const selectedText = statusItems[e];
    setFilterSelectedIndex((prev) => ({
      ...prev,
      [accessor]: e,
    }));
    getFilterMap({[accessor]: selectedText});
  };

  const handleRevieweeFilter = (e) => {
    const searchValue = e?.value ?? e?.target?.value ?? e ?? '';
    setFilterMap((prev) => ({
      ...prev,
      reviewee: searchValue,
    }));
    
    if (!searchValue) {
      setTimeout(() => {
        populateData();
      }, 100);
    }
  };

  const updateSelectedItems = (ids) => {
    const selectedItems = reviewTasks.filter((item) => ids.includes(item.id));
    setSelectedItemsState(selectedItems);
  };

  const handleCheckBoxChange = (e, id) => {
    setSelectedRowIds((prev) => {
      const updated = e.target.checked ? [...prev, id] : prev.filter((item) => item !== id);
      updateSelectedItems(updated);
      return updated;
    });
  };

  const handleSelectAllClick = (e) => {
    if (e.target.checked) {
      const allIds = reviewTasks.map((row) => row.id);
      setSelectedRowIds(allIds);
      updateSelectedItems(allIds);
    } else {
      setSelectedRowIds([]);
      setSelectedItemsState([]);
    }
  };

  /**
   * https://kentcdodds.com/blog/stop-using-isloading-booleans
   * const isLoading = status === 'idle' || status === 'pending'
   * const isResolved = status === 'resolved'/'success'
   * const isRejected = status === 'rejected'/'error'
   */
  return (
    <div className="reviewTask">
      {select}
      <EssSimpleTable
        columnData={memoizedColumns}
        filteredData={reviewTasks}
        selectedRowIds={selectedRowIds}
        onSelectAll={handleSelectAllClick}
        onSelectRow={handleCheckBoxChange}
        columnFilterSelectedIndex={filterSelectedIndex}
        onSort={(col) => getSortValue({column: col, type: sortValue?.type === 'asc' ? 'desc' : 'asc'})}
        columnFilterRefs={{
          reviewee: revieweeInputRef,
          status: {current: null},
        }}
        columnFilterHandlers={{
          reviewee: handleRevieweeFilter,
          dropdown: handleFilterDropdown,
        }}
        columnFilterDisabled={{
          status: dropdownDisabled,
        }}
        sortKey={sortValue?.column}
        sortTableDirection={sortValue?.type}
        dataCount={Array.from({length: totalItem})}
        rowsPerPage={pageSizeState}
        page={pageNoState}
        onChangePage={(e, newPage) => setPageNoState(newPage)}
        onChangeRowsPerPage={(e) => {
          setPageSizeState(parseInt(e.target.value, 10));
          setPageNoState(0);
        }}
        onPageOption={pageSize}
        onEmptyDataShow="No Data"
        hideHeader={false}
        hideFooter={false}
      />
    </div>
  );
}
